import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ArrowRight, LogIn } from "lucide-react";
import { useEffect } from "react";
import { meQuery } from "@/auth/queries";
import { ProductCard } from "@/components/ProductCard";
import { SearchForm } from "@/components/SearchForm";
import { SiteHeader } from "@/components/SiteHeader";
import { productsQuery } from "@/content/queries";
import { fr } from "@/i18n/fr";

/**
 * Landing page (`/`): the promise on an ink surface, the search card riding its bottom edge, the
 * three gestures as an editorial list, then the most assembled products, photo first. The page is
 * public, the catalogue is not: without a session the products give way to a sign-in invitation.
 */
export default function Landing() {
	const me = useQuery(meQuery());
	const popular = useQuery({ ...productsQuery("", true), enabled: !!me.data });
	useEffect(() => {
		document.title = fr.appTitle;
	}, []);
	// a query disabled by a sign-out keeps its data: show it only with a session
	const [first, ...rest] = (me.data && popular.data?.items) || [];
	return (
		<div className="page">
			<section className="relative bg-ink text-on-ink">
				<SiteHeader onInk />
				<div className="mx-auto grid max-w-6xl gap-10 px-4 pt-28 pb-20 md:grid-cols-[1.05fr_1fr] md:items-center md:gap-16 md:px-6 md:pt-36 md:pb-28">
					<div className="grid gap-5">
						<p className="text-eyebrow text-on-ink-accent uppercase">
							{fr.landing.eyebrow}
						</p>
						<h1 className="font-display text-balance text-display-lg md:text-[72px] md:leading-[72px]">
							{fr.landing.title}
						</h1>
						<p className="max-w-[520px] text-[17px] text-on-ink/70 leading-[26px]">
							{fr.landing.lead}
						</p>
					</div>
					<div
						aria-hidden="true"
						className="relative hidden h-[380px] overflow-hidden rounded-2xl bg-gradient-to-b from-scene-top to-scene-bottom md:block"
					>
						<span className="pill absolute top-4 left-4 text-fg">
							{fr.recenter}
						</span>
						<div className="absolute inset-x-5 bottom-5 grid gap-2 rounded-xl bg-bg-elev px-5 py-4 text-fg shadow-lg">
							<p className="text-eyebrow text-accent uppercase">
								{fr.stepOf(4, 12)}
							</p>
							<p className="font-display text-2xl leading-7">
								Fixer le plateau
							</p>
							<p className="text-fg-muted">
								Enfoncer les chevilles jusqu’au clic.
							</p>
							<div className="mt-1 h-1.5 overflow-hidden rounded-sm bg-bg-sunken">
								<i className="block h-full w-2/3 bg-accent" />
							</div>
						</div>
					</div>
				</div>
			</section>

			{/* the search rides the hero's bottom edge */}
			<div className="mx-auto max-w-6xl px-4 md:px-6">
				<div className="-mt-10 relative z-[1] grid gap-3 rounded-xl bg-bg-elev p-3 shadow-lg md:-mt-12 md:max-w-[720px] md:p-4">
					<SearchForm hero />
					<p className="px-2 text-[13px] text-fg-muted leading-[18px]">
						{fr.landing.examples}{" "}
						{fr.landing.exampleQueries.map((q, i) => (
							<span key={q}>
								{i > 0 && " · "}
								<Link to="/catalogue" search={{ q }} className="text-accent">
									{q}
								</Link>
							</span>
						))}
					</p>
				</div>

				<section className="grid gap-6 py-16 md:grid-cols-[1fr_2fr] md:gap-12 md:py-24">
					<h2 className="font-display text-[32px] text-balance leading-9 tracking-tight">
						{fr.landing.howTitle}
					</h2>
					<ol className="m-0 list-none divide-y divide-line p-0">
						{fr.landing.how.map((item, i) => (
							<li
								key={item.title}
								className="grid grid-cols-[64px_1fr] items-baseline gap-4 py-5 first:pt-0 md:grid-cols-[96px_1fr]"
							>
								<span className="font-display text-[40px] text-accent leading-none md:text-[56px]">
									{String(i + 1).padStart(2, "0")}
								</span>
								<span>
									<b className="block font-semibold text-lg">{item.title}</b>
									<span className="text-fg-muted">{item.text}</span>
								</span>
							</li>
						))}
					</ol>
				</section>

				{first && (
					<section className="grid gap-8 border-line border-t py-16 md:py-24">
						<div className="flex items-baseline justify-between gap-4">
							<h2 className="font-display text-[32px] leading-9 tracking-tight">
								{fr.landing.popular}
							</h2>
							<Link
								to="/catalogue"
								className="inline-flex items-center gap-1 font-medium text-sm"
							>
								{fr.landing.allCatalog}
								<ArrowRight className="size-4" aria-hidden="true" />
							</Link>
						</div>
						<ProductCard product={first} featured />
						{rest.length > 0 && (
							<ul className="grid list-none grid-cols-2 gap-4 p-0 md:grid-cols-4 md:gap-6">
								{rest.slice(0, 4).map((product) => (
									<li key={product.slug}>
										<ProductCard product={product} />
									</li>
								))}
							</ul>
						)}
					</section>
				)}

				{me.data === null && (
					<section className="grid justify-items-start gap-4 border-line border-t py-16 md:py-24">
						<h2 className="font-display text-[32px] text-balance leading-9 tracking-tight">
							{fr.landing.membersTitle}
						</h2>
						<p className="max-w-[520px] text-fg-muted">
							{fr.landing.membersText}
						</p>
						<Link to="/connexion" className="ui-btn-primary no-underline">
							<LogIn className="size-5" aria-hidden="true" />
							{fr.signIn}
						</Link>
					</section>
				)}

				<footer className="flex flex-wrap gap-x-5 gap-y-3 border-line border-t py-8 text-fg-muted text-xs">
					<span>{fr.appTitle}</span>
					<span>{fr.landing.disclaimer}</span>
					<a href="/" className="text-fg-muted">
						{fr.landing.privacy}
					</a>
				</footer>
			</div>
		</div>
	);
}
